<script lang="ts">
  import { DECK, NODE_IDS, neighbors, transitProgress, travelTicks, TICK_HZ, type NodeId } from "@spacecrew/shared";

  /** compact: movement controls only (for the in-CRT terminal); the schematic is the M1 debug view. */
  let { compact = false }: { compact?: boolean } = $props();
  import { ship } from "../net/ship.svelte.js";
  import { session } from "../net/session.svelte.js";
  import { send } from "../net/intents.js";

  const snap = $derived(ship.snap!);
  const me = $derived(snap.crew[session.sessionId]);
  const canMove = $derived(snap.phase === "running" && !!me && me.toNodeId === "");
  const adjacent = $derived(me ? neighbors(me.nodeId as NodeId) : []);

  const W = 100, H = 60;
  const px = (xy: readonly [number, number]) => ({ x: xy[0] * W, y: xy[1] * H });

  function dot(c: (typeof snap.crew)[string]) {
    const a = px(DECK.nodes[c.nodeId as NodeId].xy);
    if (!c.toNodeId) return a;
    const b = px(DECK.nodes[c.toNodeId as NodeId].xy);
    const t = transitProgress(snap.tick, c.departTick, c.arriveTick);
    return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
  }
</script>

<section aria-labelledby="deck-title">
  <h2 id="deck-title">{compact ? "HATCHES" : "DECK SCHEMATIC"}</h2>
  {#if !compact}
  <svg viewBox="0 0 {W} {H}" role="img" aria-label="Deck map with crew positions">
    {#each DECK.edges as e}
      {@const a = px(DECK.nodes[e.a].xy)}{@const b = px(DECK.nodes[e.b].xy)}
      <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="var(--fg-dim)" stroke-width="0.6" stroke-dasharray={e.kind === "crawlspace" ? "1 1" : e.kind === "ladder" ? "2 1.5" : undefined} />
    {/each}
    {#each NODE_IDS as id}
      {@const p = px(DECK.nodes[id].xy)}
      <circle cx={p.x} cy={p.y} r="3" fill="none" stroke={me?.nodeId === id && !me.toNodeId ? "var(--fg)" : "var(--fg-dim)"} stroke-width="0.6" />
      <text x={p.x} y={p.y + 7} font-size="3" text-anchor="middle" fill="var(--fg-dim)">{DECK.nodes[id].label}</text>
    {/each}
    {#each Object.entries(snap.crew) as [id, c] (id)}
      {@const p = dot(c)}
      <circle cx={p.x} cy={p.y} r="1.4" fill={id === session.sessionId ? "var(--fg)" : "var(--warn)"} opacity={c.connected ? 1 : 0.4} />
      <text x={p.x} y={p.y - 2.5} font-size="2.4" text-anchor="middle" fill={id === session.sessionId ? "var(--fg)" : "var(--warn)"}>{c.name}</text>
    {/each}
  </svg>
  {/if}

  <div class="moves" role="group" aria-label="Move to">
    {#if me?.toNodeId}
      <span class="dim">IN TRANSIT → {DECK.nodes[me.toNodeId as NodeId].label} ({Math.max(0, (me.arriveTick - snap.tick) / TICK_HZ).toFixed(1)} s)</span>
    {:else}
      {#each adjacent as n}
        <button disabled={!canMove} onclick={() => send("move.to", { nodeId: n })}>
          GO TO {DECK.nodes[n].label} ({(travelTicks(me!.nodeId as NodeId, n)! / TICK_HZ).toFixed(0)} s)
        </button>
      {/each}
    {/if}
  </div>
</section>

<style>
  svg { width: 100%; max-width: 520px; display: block; border: 1px solid var(--fg-dim); }
  .moves { display: flex; gap: 0.5rem; flex-wrap: wrap; margin-top: 0.5rem; min-height: 44px; align-items: center; }
</style>
